import type { IngestRequest } from '@feedback/shared';
import { ApiError, AuthError, RateLimitError, WidgetApiError } from './types';
import type { SubmitResult, UploadMeta, UploadTarget, WidgetConfig } from './types';

export interface CreateApiClientOptions {
  /** Backend origin, e.g. `https://feedback.example.com` (no trailing slash assumed). */
  apiBase: string;
  /** The integration key sent as `X-Feedback-Key` on every request. */
  key: string;
}

export interface ApiClient {
  getConfig(): Promise<WidgetConfig>;
  createUpload(meta: UploadMeta): Promise<UploadTarget>;
  putBlob(url: string, blob: Blob, contentType: string): Promise<void>;
  submitRequest(payload: IngestRequest): Promise<SubmitResult>;
}

/**
 * Parses a non-2xx `Response` into the appropriate typed `WidgetApiError`
 * subclass. Reads the body as the backend's standard error envelope
 * (`{ error: { code, message } }`) when possible; falls back to
 * `res.statusText` if the body isn't JSON (or is empty, e.g. from Azure Blob
 * Storage on a failed SAS PUT).
 */
async function toWidgetApiError(res: Response): Promise<WidgetApiError> {
  let code: string | undefined;
  let message = res.statusText || `Request failed with status ${res.status}`;

  try {
    const body = (await res.json()) as { error?: { code?: string; message?: string } };
    if (body?.error?.code) code = body.error.code;
    if (body?.error?.message) message = body.error.message;
  } catch {
    // Non-JSON or empty body — keep the statusText-derived message above.
  }

  if (res.status === 401 || res.status === 403) {
    return new AuthError(message, res.status, code);
  }
  if (res.status === 429) {
    return new RateLimitError(message, res.status, code);
  }
  return new ApiError(message, res.status, code);
}

/**
 * Creates the typed fetch wrapper the widget uses to talk to the ingest API.
 * Every call (except `putBlob`, which targets Azure Blob Storage directly)
 * sends `X-Feedback-Key` + `Content-Type: application/json` and maps non-2xx
 * responses to typed errors from `./types`.
 *
 * This module never swallows errors — it throws for callers to catch. The
 * widget's fail-silent policy toward the host app is enforced by the
 * controller that consumes this client, not here.
 */
export function createApiClient({ apiBase, key }: CreateApiClientOptions): ApiClient {
  // Normalize any trailing slash so a consumer passing `https://api.example.com/`
  // doesn't produce `https://api.example.com//api/v1/...` (double slash).
  const base = `${apiBase.replace(/\/+$/, '')}/api/v1`;

  async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const res = await fetch(`${base}${path}`, {
      ...init,
      headers: {
        'X-Feedback-Key': key,
        'Content-Type': 'application/json',
        ...init.headers,
      },
    });

    if (!res.ok) {
      throw await toWidgetApiError(res);
    }

    return (await res.json()) as T;
  }

  return {
    getConfig() {
      return request<WidgetConfig>('/widget/config', { method: 'GET' });
    },

    createUpload(meta) {
      return request<UploadTarget>('/ingest/attachments', {
        method: 'POST',
        body: JSON.stringify(meta),
      });
    },

    async putBlob(url, blob, contentType) {
      const res = await fetch(url, {
        method: 'PUT',
        headers: {
          'x-ms-blob-type': 'BlockBlob',
          'Content-Type': contentType,
        },
        body: blob,
      });

      if (!res.ok) {
        // Route through the same status→error mapping as the rest of the
        // client so callers get a consistent error type (401/403 → AuthError,
        // 429 → RateLimitError, else ApiError). `toWidgetApiError` tolerates
        // Azure's non-JSON (XML/empty) bodies via its try/catch fallback.
        throw await toWidgetApiError(res);
      }
    },

    submitRequest(payload) {
      return request<SubmitResult>('/ingest/requests', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
    },
  };
}
